/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import template from './api-key-edit-page.component.html';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {ApiConfig} from '../../../components/api/api';
import {API_KEY_SCOPE_OPTIONS} from './api-key-commons';
import {AdnHttpService} from '../../../components/util/adn-http.service';
import {
  AdnHttpSender,
  createAdnHttpResourceSender
} from '../../../components/util/create-adn-http-resource';
import {AuthHttpInterceptorService} from '../../../components/auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../common/directives/error/api-error-handler.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';

@Component({
  standalone: false,
  selector: 'adn-api-key-edit-page',
  template
})
export class ApiKeyEditPageComponent extends AdminResourceEditPageBase {
  readonly scopeOptions = API_KEY_SCOPE_OPTIONS;
  private readonly send: AdnHttpSender;

  revoked = false;
  revokeError: any = null;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    AdnHttp: AdnHttpService,
    authHttpInterceptor: AuthHttpInterceptorService,
    ApiErrorHandler: ApiErrorHandlerService,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  }

  hasScope(scope: string): boolean {
    return (this.editingModel.scopes || []).indexOf(scope) > -1;
  }

  scopesValid(): boolean {
    return !!this.editingModel.fullAccess || (this.editingModel.scopes || []).length > 0;
  }

  onScopesChanged(): void {
    this.markExternalFormDirty();
  }

  onFullAccessChanged(): void {
    this.markExternalFormDirty();
  }

  onExpiryChanged(): void {
    this.markExternalFormDirty();
  }

  revoke(): Promise<void> {
    this.revokeError = null;
    const endpoint = ApiConfig.obtain().getVersionedUri('apikeys/' + this.editingModel.id + '/revoke');
    return this.send({method: 'POST', url: endpoint, body: {}}).then((data: any) => {
      this.ngZone.run(() => {
        this.revoked = true;
        this.editingModel.expired = true;
        Object.assign(this.model, {expired: true});
        data = data || {};
        if (data.expiry) {
          this.editingModel.expiry = data.expiry;
          this.model.expiry = data.expiry;
        }
      });
    }, (response: any) => {
      this.ngZone.run(() => {
        this.revokeError = (response && response.data) ? response.data : {message: 'Revoke failed.'};
      });
    });
  }

  protected afterInitEditingModel(): void {
    this.editingModel.scopes = this.editingModel.scopes || [];

    if (this.isNew) {
      this.editingModel.fullAccess = false;
    }

    if (this.editingModel.expiry && !this.editingModel.modExpiry) {
      this.editingModel.modExpiry = this.editingModel.expiry;
    }
  }

  protected afterInitAfterSave(saveObj?: any): void {
    if (saveObj?.apiKey) {
      this.editingModel.apiKey = saveObj.apiKey;
      this.editingModel.expiry = saveObj.expiry;
    }
    if (this.editingModel.expiry) {
      this.editingModel.modExpiry = this.editingModel.expiry;
    }
  }

  protected onBeforeSubmit(): void {
    if (this.editingModel.fullAccess) {
      this.editingModel.scopes = ['NG_API'];
    } else {
      this.editingModel.scopes = this.editingModel.scopes || [];
      _.remove(this.editingModel.scopes, (element: string) => element === 'NG_API');
    }

    this.editingModel.expiry = _.isDate(this.editingModel.modExpiry) ? this.editingModel.modExpiry : null;
  }
}
